import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { usePanelist, usePublicPanel, useTasks } from "../../api";
import type { Panelist } from "../../types";
import { pageTitle } from "../../utils";
import RouteDebug from "../dev/RouteDebug";
import { AppFooter } from "./HelpSupport";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

const placeholderPanelist: Panelist = {
  id: 0,
  firstName: "–",
  lastName: "–",
  email: "–",
  memberSince: "–",
  points: 0,
  pointsGbp: "£0.00",
  pointsToNext: null,
  profilePct: 0,
  tasksCompleted: 0,
  totalEarnedGbp: null,
  totalEarnedPts: null,
  isCheckinDue: false,
};

export function AppLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const panelistQuery = usePanelist();
  const tasksQuery = useTasks();
  const publicPanel = usePublicPanel();
  const panelist = panelistQuery.data ?? placeholderPanelist;
  const taskCount = tasksQuery.data?.length ?? 0;

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") setSidebarOpen(false);
    }

    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, []);

  return (
    <div className={`app ${sidebarOpen ? "sidebar-open" : ""}`.trim()}>
      <RouteDebug label="AppLayout" />
      <Sidebar
        onClose={() => setSidebarOpen(false)}
        panelist={panelist}
        taskCount={taskCount}
      />
      <button
        aria-label="Close navigation"
        className={`mobile-sidebar-backdrop ${sidebarOpen ? "is-open" : ""}`.trim()}
        onClick={() => setSidebarOpen(false)}
        type="button"
      />
      <div className="main">
        <Topbar
          onMenuClick={() => setSidebarOpen((open) => !open)}
          title={pageTitle(location.pathname)}
        />
        <main className="content">
          <Outlet />
        </main>
        <AppFooter supportEmail={publicPanel.data?.supportEmail ?? ""} />
      </div>
    </div>
  );
}
